import type { Control, FieldValues, UseFormRegister, FieldErrors, UseFormWatch, UseFormSetValue } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { TextBoxField, Select, Checkbox, TimeZoneCombobox } from '@/components'
import { SlugInput } from '@/components/common/SlugInput'
import { workspaceApi } from '@/api'

/**
 * Shared form values for workspace fields.
 * Parent forms should include these fields in their form type.
 */
export interface WorkspaceBaseFormValues {
    Name: string
    Description: string
    Status: string
    TimeZone: string
    DailyLeadsSummaryEnabled: boolean
    DailyLeadsSummaryHour: number
    WebsterEnabled?: boolean // Edit flow only (see showWebsterToggle)
}

/**
 * The subset of the form this component drives through `control` — the two TextBoxFields and the
 * TimeZone Controller. Everything else it renders goes through `register`, which stays untyped.
 *
 * Deliberately narrower than WorkspaceBaseFormValues: AddCustomerModal's workspace form does not
 * carry the daily-summary fields at all, and constraining on fields `control` never touches would
 * lock it out for no benefit.
 */
interface WorkspaceControlledFields {
    Name: string
    Description?: string
    TimeZone: string
}

interface WorkspaceFormFieldsProps<T extends WorkspaceControlledFields & FieldValues> {
    /**
     * Generic rather than `Control<any>`: since react-hook-form 7.83 `Control` carries a
     * `_options.validate` whose field-name union resolves through the form type, so `Control<any>`
     * no longer accepts a concrete form and `Control<FieldValues>` never did. A type parameter is
     * the only widening that lets all three callers pass their own form shape.
     */
    control: Control<T>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    register: UseFormRegister<any>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errors: FieldErrors<any>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    watch: UseFormWatch<any>
    /**
     * Required: SlugInput pushes its value back into RHF state via setValue.
     * Without this the slug field stays empty, the hidden required-Slug
     * registration keeps the form invalid, and Save is permanently disabled.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setValue: UseFormSetValue<any>
    /**
     * When provided, the slug input runs a live availability check against
     * this ClientCompanyId. Omit for create-new-company flows where the
     * company and its initial workspace are created in the same request and
     * no collisions are possible (exactly one workspace will exist).
     */
    clientCompanyIdForSlugCheck?: string
    /** Locks the slug field. Set on the edit flow: a workspace slug is fixed at creation. */
    slugLocked?: boolean
    /**
     * Show the workspace-level Webster Notetaker master toggle. Edit flow only: new workspaces
     * default Webster on server-side, and the create DTO doesn't carry the field.
     */
    showWebsterToggle?: boolean
}

/**
 * Re-narrows a caller's `control` to the fields this component actually drives.
 *
 * react-hook-form's `Control` is invariant in its form type, so a form that merely *contains* the
 * workspace fields is still not assignable to `Control<WorkspaceControlledFields>`. The constraint
 * on `T` is what guarantees those fields exist; this hands the render body a concrete `Control` so
 * the field names below stay literal and a typo in one is still a compile error.
 *
 * Extracted so the cast has one documented home rather than sitting inside the render body.
 */
const asWorkspaceControl = <T extends WorkspaceControlledFields & FieldValues>(
    control: Control<T>,
): Control<WorkspaceControlledFields> => control as unknown as Control<WorkspaceControlledFields>

const NAME_MAX = 200
const DESC_MAX = 1000

const STATUS_OPTIONS = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
    { value: 'Archived', label: 'Archived' },
    { value: 'Prospect', label: 'Prospect' },
]

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({
    value: h,
    label: h === 0 ? '12:00 AM' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`,
}))

/**
 * Shared workspace form fields: Name, Description, Status, Time Zone, Daily Leads Summary.
 * Used by WorkspaceCard (edit), WorkspaceEditForm (create), and AddCustomerModal (create).
 *
 * `MyForm` needs at least Name, Description and TimeZone — see WorkspaceControlledFields. The form
 * type is inferred from `control`; nothing here has to be named at the call site.
 *
 * @example
 * ```tsx
 * const { register, control, watch, setValue, formState: { errors } } = useForm<MyForm>()
 * <WorkspaceFormFields control={control} register={register} errors={errors} watch={watch} setValue={setValue} />
 * ```
 */
export function WorkspaceFormFields<T extends WorkspaceControlledFields & FieldValues>({ control, register, errors, watch, setValue, clientCompanyIdForSlugCheck, slugLocked, showWebsterToggle }: WorkspaceFormFieldsProps<T>) {
    const workspaceControl = asWorkspaceControl(control)

    return (
        <>
            <TextBoxField
                control={workspaceControl}
                name="Name"
                label="Workspace Name"
                required
                showCharCounter
                maxLength={NAME_MAX}
                placeholder="Enter workspace name"
                rules={{
                    required: 'Workspace name is required',
                    maxLength: { value: NAME_MAX, message: `Workspace name cannot exceed ${NAME_MAX} characters.` },
                }}
            />
            <SlugInput
                label="Slug"
                required
                value={(watch('Slug') as string) || ''}
                onChange={(v) => setValue('Slug', v, { shouldValidate: true, shouldDirty: true })}
                sourceName={watch('Name') as string}
                error={errors.Slug?.message as string | undefined}
                locked={slugLocked}
                lockedMessage="Slug is set at creation and cannot be changed."
                checkAvailability={clientCompanyIdForSlugCheck ? async (slug) => {
                    const res = await workspaceApi.checkSlug(clientCompanyIdForSlugCheck, slug)
                    return res.data ?? { available: false }
                } : undefined}
                helperText="URL segment for deployed landing pages in this workspace."
            />
            {/* Hidden registration so the form tracks slug for submission/validation */}
            <input type="hidden" {...register('Slug', {
                required: 'Slug is required',
                pattern: { value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/, message: 'Slug must be lowercase letters, numbers, and single hyphens.' },
                minLength: { value: 3, message: 'Slug must be at least 3 characters.' },
                maxLength: { value: 50, message: 'Slug must be 50 characters or fewer.' },
            })} />
            <TextBoxField
                control={workspaceControl}
                name="Description"
                label="Description"
                multiline
                rows={3}
                showCharCounter
                maxLength={DESC_MAX}
                placeholder="Optional description for this workspace"
                rules={{
                    maxLength: { value: DESC_MAX, message: `Description cannot exceed ${DESC_MAX} characters.` },
                }}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Select
                    label="Status"
                    required
                    options={STATUS_OPTIONS}
                    placeholder="Select status"
                    error={errors.Status?.message as string}
                    {...register('Status', { required: 'Status is required' })}
                />
                <Controller
                    name="TimeZone"
                    control={workspaceControl}
                    rules={{ required: 'Time zone is required' }}
                    render={({ field, fieldState }) => (
                        <TimeZoneCombobox
                            label="Time Zone"
                            required
                            name="TimeZone"
                            value={field.value}
                            onChange={field.onChange}
                            error={fieldState.error?.message as string | undefined}
                        />
                    )}
                />
            </div>
            <Checkbox
                label="Daily Leads Summary"
                description="Send a daily email to all workspace members summarizing new leads from the previous 24 hours. Emails are sent Monday–Friday mornings. No email is sent on days with zero leads."
                {...register('DailyLeadsSummaryEnabled')}
            />
            {watch('DailyLeadsSummaryEnabled') && (
                <div className="ml-7 flex items-center gap-2">
                    <Select
                        label="Send at"
                        labelSize="xs"
                        options={HOUR_OPTIONS}
                        placeholder="Select hour"
                        containerClassName="w-40"
                        {...register('DailyLeadsSummaryHour', { valueAsNumber: true })}
                    />
                    <span className="text-xs text-slate-500 dark:text-slate-400 mt-5">(workspace time zone)</span>
                </div>
            )}
            {showWebsterToggle && (
                <Checkbox
                    label="Webster Notetaker"
                    description="Let Webster join Google Meet and Teams meetings in this workspace to record and transcribe them. Turn it off to stop scheduling new meeting bots across the workspace."
                    {...register('WebsterEnabled')}
                />
            )}
        </>
    )
}
